import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container } from "reactstrap";

import DemoNavbar from "components/Navbars/DemoNavbar.js";
import StudioMeter, { planBannerText, planStatusLabel } from "components/Studio/StudioMeter";
import { useUserAuth } from "context/UserContext";
import useReveal from "hooks/useReveal";
import { isStudioConfigured, studioApi } from "services/studioService";
import { errorText, formatDate, projectStatus, tierLabel } from "components/Studio/studioText";
import "../../styles/brand-auth.css";
import "./studio.css";

/* /studio: the client's projects, their plan and what is left of it, and
   "New project". Invite-only: users/{uid}.studioEnabled is set by Codenec,
   and the Studio service checks it on every call; this page only uses it to
   explain.

   Back from a plan checkout (?plan=success) the page asks the Studio every 3
   seconds, for at most 30 seconds, until the plan is active. The status line
   changes only when its message does, so a screen reader hears "Confirming",
   then "active" or "still confirming", never each poll. */

const POLL_MS = 3000;
const POLL_FOR_MS = 30000;

function StudioHome() {
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ loading: true, projects: [], me: null, error: null });
  // The backend's view of the subscription (past due, ending), when it answers.
  const [billing, setBilling] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  // "confirming" | "active" | "still" | null: the return from a plan checkout.
  const [confirm, setConfirm] = useState(() => (searchParams.get("plan") === "success" ? "confirming" : null));

  const invited = user?.studioEnabled === true;
  const lang = i18n.language;

  const userRef = useRef(user);
  userRef.current = user;
  const loadRef = useRef(null);

  const load = useCallback(async () => {
    if (!isStudioConfigured || !invited) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    studioApi.plan(user).then((data) => setBilling(data?.billing ?? null)).catch(() => {});
    try {
      const [me, list] = await Promise.all([studioApi.me(user), studioApi.listProjects(user)]);
      setState({ loading: false, projects: list.projects, me, error: null });
    } catch (err) {
      setState({ loading: false, projects: [], me: null, error: errorText(t, err) });
    }
  }, [user, invited, t]);

  loadRef.current = load;
  useEffect(() => {
    load();
  }, [load]);

  // The plan-checkout return poll.
  useEffect(() => {
    if (confirm !== "confirming" || !isStudioConfigured || !invited) return undefined;
    let done = false;
    const started = Date.now();
    const check = async () => {
      try {
        const me = await studioApi.me(userRef.current);
        if (done) return;
        setState((s) => ({ ...s, me }));
        if (me?.plan?.status === "active") {
          done = true;
          setConfirm("active");
          loadRef.current?.();
          return;
        }
      } catch {
        // next tick
      }
      if (!done && Date.now() - started >= POLL_FOR_MS) {
        done = true;
        setConfirm("still");
      }
    };
    check();
    const timer = setInterval(() => {
      if (done) clearInterval(timer);
      else check();
    }, POLL_MS);
    return () => {
      done = true;
      clearInterval(timer);
    };
  }, [confirm, invited]);

  const plan = state.me?.plan || null;
  const limit = state.me?.limits?.apps ?? state.me?.limits?.projectsPerOwner ?? 3;
  const atLimit = state.projects.length >= limit;
  // No plan, trial over or plan ended: new projects need a plan.
  const needsPlan = ["none", "trial_ended", "lapsed"].includes(plan?.status);
  const createOff = atLimit || needsPlan || creating;
  const createReason = needsPlan ? t("studio.home.needsPlan") : atLimit ? (plan?.source === "trial" ? t("studio.home.limitTrial") : t("studio.home.limit", { count: limit })) : null;

  const create = async (event) => {
    event.preventDefault();
    if (!name.trim() || createOff) return;
    setCreating(true);
    setCreateError(null);
    try {
      const { project } = await studioApi.createProject(user, name);
      navigate(`/studio/${project.id}`);
    } catch (err) {
      setCreateError(errorText(t, err));
      setCreating(false);
    }
  };

  // Re-scan once the list arrives, so the project cards rise in after it.
  // Keyed on the count alone: a re-scan settles anything still waiting, so
  // keying on `loading` too would cut the band's entrance short whenever
  // there is nothing to fetch.
  useReveal([state.projects.length]);

  const banner = planBannerText(t, plan, billing, lang);
  const confirmText = confirm ? t(`studio.plan.return.${confirm}`) : "";

  return (
    <>
      <DemoNavbar />
      <main className="studio studio-home">
        <section className="studio-home__band cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <Container style={{ position: "relative", zIndex: 2 }}>
            <div className="studio-eyebrow" data-reveal>{t("studio.home.eyebrow")}</div>
            <h1 data-reveal style={{ "--reveal-delay": "60ms" }}>{t("studio.home.title")}</h1>
            <p data-reveal style={{ "--reveal-delay": "120ms" }}>{t("studio.home.lead")}</p>
          </Container>
        </section>

        <Container className="studio-home__shell">
          <div className="studio-panel" data-reveal style={{ "--reveal-delay": "180ms" }}>
            {/* The plan-checkout return: one status line, always in the page. */}
            <div role="status" className={confirm ? "studio-notice studio-plan-return" : "sr-only"}>
              {confirm && <p>{confirmText}</p>}
              {confirm === "still" && (
                <button type="button" className="studio-btn studio-btn-small" onClick={() => setConfirm("confirming")}>
                  {t("studio.plan.return.checkAgain")}
                </button>
              )}
            </div>

            {!isStudioConfigured ? (
              <div className="studio-notice"><p>{t("studio.home.notConfigured")}</p></div>
            ) : !invited ? (
              <div className="studio-notice"><p>{t("studio.home.inviteOnly")}</p></div>
            ) : state.loading ? (
              <p className="studio-muted studio-loading">{t("studio.common.loading")}</p>
            ) : state.error ? (
              <div className="studio-notice"><p>{state.error}</p></div>
            ) : (
              <>
                {plan && (
                  <section className="studio-plan-block" aria-labelledby="studio-plan-heading">
                    <div className="studio-plan-block__head">
                      <h2 id="studio-plan-heading">{t("studio.home.planTitle")}</h2>
                      {plan.tier && <span className="studio-chip">{tierLabel(t, plan.tier)}</span>}
                      <span className="studio-chip">{planStatusLabel(t, plan, billing)}</span>
                    </div>
                    <StudioMeter plan={plan} locale={lang} />
                    {banner && <div className="studio-notice"><p>{banner}</p></div>}
                    <div className="studio-plan-block__actions">
                      {(plan.source === "stripe" || plan.source === "comp") && (
                        <Link className="studio-btn studio-btn-ghost studio-btn-small" to="/accountsettings?tab=billing">
                          {t("studio.plan.managePlan")}
                        </Link>
                      )}
                      <Link className="studio-btn studio-btn-ghost studio-btn-small" to="/studio/plans">
                        {t("studio.plan.seePlans")}
                      </Link>
                    </div>
                  </section>
                )}

                <form className="studio-new" onSubmit={create}>
                  <input
                    className="studio-input"
                    value={name}
                    maxLength={60}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("studio.home.namePlaceholder")}
                    aria-label={t("studio.home.nameLabel")}
                    readOnly={createOff}
                    aria-describedby={createReason ? "studio-new-reason" : undefined}
                  />
                  <button
                    className="studio-btn studio-btn-primary"
                    type="submit"
                    aria-disabled={createOff || !name.trim() ? "true" : undefined}
                    aria-describedby={createReason ? "studio-new-reason" : undefined}
                  >
                    {creating ? t("studio.home.creating") : t("studio.home.create")}
                  </button>
                </form>
                {createError && <div className="studio-notice" role="alert"><p>{createError}</p></div>}
                {createReason && (
                  <div className="studio-muted" id="studio-new-reason">
                    {createReason} {(needsPlan || plan?.source === "trial") && <Link to="/studio/plans">{t("studio.plan.seePlans")}</Link>}
                  </div>
                )}

                {state.projects.length === 0 ? (
                  <p className="studio-muted studio-empty" style={{ marginTop: 24 }}>{t("studio.home.empty")}</p>
                ) : (
                  <div className="studio-projects">
                    {state.projects.map((project, i) => {
                      const status = projectStatus(t, project);
                      // The reveal goes on a wrapper: the card's own hover
                      // transition would otherwise replace the reveal's.
                      return (
                        <div
                          key={project.id}
                          className="studio-projects__item"
                          data-reveal
                          style={{ "--reveal-delay": `${(i % 3) * 60}ms` }}
                        >
                          <Link className="studio-project" to={`/studio/${project.id}`}>
                            <h3>{project.name}</h3>
                            <div className="studio-project__meta">
                              <span className={`studio-chip ${status.chip}`}>{status.label}</span>
                              <span className="studio-muted">{t("studio.home.updated", { date: formatDate(project.updatedAt, i18n.language) })}</span>
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </Container>
      </main>
    </>
  );
}

export default StudioHome;
